import React from 'react';
import Markdown from 'markdown-to-jsx';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Image from '@atlaskit/image';
import { Box, Stack } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages';
import taskMessages from '../../../common/utils/tasks/messages';
import TaskFourIllustration from './assets/alerts-task-four-illustration.png';
import TaskTwoIllustrationDark from './assets/alerts-task-two-illustration-dark.png';
import TaskTwoIllustration from './assets/alerts-task-two-illustration.png';
import { messages } from './messages';

const Bold = ({ children }: { children: string[] }) => (
	<Box as="strong">
		{children} {children?.[0] === 'More actions' && <MoreIcon size="small" label="" />}
	</Box>
);

export const AlertsStep = () => {
	const { formatMessage } = useIntl();

	const isOptimalTooling = ff('platform.show-optimal-tooling_brgom');

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={
					isOptimalTooling
						? formatMessage(stepMessages.alertsAndOnCallTitleNonFinal)
						: formatMessage(stepMessages.alertsTitleNonFinal)
				}
				description={<FormattedMessage {...stepMessages.alertsDescriptionNonFinal} />}
			/>
			{/* Task One */}
			<TransitionHomeContentBox>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.alertsTaskOneNonFinal} />
					</Heading>
					<Box>
						<Markdown>{formatMessage(messages.taskOneDescriptionNonFinal)}</Markdown>
					</Box>
					<Box>
						<Button href="/jira/ops/alerts" target="_blank">
							<FormattedMessage {...messages.taskOneButtonNonFinal} />
						</Button>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Two */}
			<TransitionHomeContentBox
				illustration={<Image src={TaskTwoIllustration} srcDark={TaskTwoIllustrationDark} alt="" />}
			>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.alertsTaskTwoNonFinal} />
					</Heading>
					<Box>
						<Markdown
							options={{
								overrides: {
									strong: {
										component: Bold,
									},
								},
							}}
						>
							{formatMessage(messages.taskTwoDescriptionNonFinal)}
						</Markdown>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Three */}
			<TransitionHomeContentBox>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.alertsTaskThreeNonFinal} />
					</Heading>
					<Box>
						<Markdown>{formatMessage(messages.taskThreeDescriptionNonFinal)}</Markdown>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{isOptimalTooling && (
				<>
					{/* Task Four */}
					<TransitionHomeContentBox
						illustration={
							<Image
								// todo: change the illustration when dark mode is ready
								src={TaskFourIllustration}
								srcDark={TaskFourIllustration}
								alt=""
							/>
						}
					>
						<Stack space="space.200">
							<Heading as="h2" size="small">
								{formatMessage(taskMessages.alertsTaskFourNonFinal)}
							</Heading>
							<Box>
								<Markdown>{formatMessage(messages.taskFourDescriptionNonFinal)}</Markdown>
							</Box>
							<Box>
								<Button href="/jira/ops/who-is-on-call" target="_blank">
									{formatMessage(messages.taskFourButtonNonFinal)}
								</Button>
							</Box>
						</Stack>
					</TransitionHomeContentBox>
				</>
			)}
		</Stack>
	);
};
